import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GithubLogoIcon, ArrowUpRightIcon } from '@phosphor-icons/react';

import { Alert } from '@/ui/Alert';
import { SpinnerBlock } from '@/ui/Spinner';
import { fetchMe } from '@/api-models/user';
import { AuthLayout, OAuthButton } from '@/features/auth/components';
import { AUTH_FEATURES, AUTH_HEADLINE } from '@/features/auth/components/authMarketing';
import { useSessionStore } from '@/stores/session-store';

const AUTH_STATS = [
  { value: '50K+', label: 'developers building' },
  { value: '2.4s', label: 'avg workspace ready' },
  { value: '1M+', label: 'AI tasks completed' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const clearExpired = useSessionStore((s) => s.clearExpired);

  // Set by AuthProvider when it bounces someone whose session died mid-session. Without
  // the explanation the redirect just looks like being randomly signed out.
  const sessionExpired = searchParams.get('expired') === '1';

  useEffect(() => {
    clearExpired();
  }, [clearExpired]);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const data = await fetchMe();
        if (data.id) {
          navigate('/app');
        }
      } catch {
        // Not authenticated, stay on login page
      } finally {
        setIsLoading(false);
      }
    };
    checkAuthStatus();
  }, [navigate]);

  if (isLoading) {
    return <SpinnerBlock message="Loading..." className="h-screen" />;
  }

  return (
    <AuthLayout
      headline={AUTH_HEADLINE}
      subline="Cloud workspaces where you and AI agents build together. No setup, no friction — just open a browser and start shipping."
      features={AUTH_FEATURES}
      stats={AUTH_STATS}
      pageTitle="Sign in — Zooid"
    >
      <div className="flex flex-col items-center text-center">
        {sessionExpired && (
          <Alert variant="warning" className="mb-6 w-full max-w-80 text-left">
            Your session expired. Please sign in again.
          </Alert>
        )}

        {/* Header */}
        <h2 className="text-display font-bold tracking-tight text-fg mb-3">Sign in to Zooid</h2>
        <p className="mt-2 text-md text-fg-secondary">Start building with AI in seconds</p>

        {/* OAuth Button */}
        <div className="mt-10 w-full max-w-80">
          <OAuthButton
            icon={<GithubLogoIcon size={20} weight="fill" />}
            label="Continue with GitHub"
            onClick={() => {
              window.location.href = '/api/v1/auth/github';
            }}
          />
        </div>

        {/* Footer */}
        <p className="mt-8 text-2xs text-fg-muted">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
        <div className="mt-4 flex items-center gap-1.5">
          <span className="text-xs text-fg-secondary">No invite yet?</span>
          <Link
            to="/signup"
            className="flex items-center gap-1 text-xs font-semibold text-brand-text hover:text-brand-hover"
          >
            Request beta access
            <ArrowUpRightIcon size={14} />
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}
